import { AxiosError } from 'axios'
import { CheckCircle2, ClipboardList } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCampaignForReader,
	useSubmitCampaignResponse,
} from '@/api/queries/campaigns-queries'
import { CampaignQuestionInput } from '@/components/campaigns/campaign-question-input'
import { AdminCardPage } from '@/components/commons'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { ROUTES } from '@/constants/routes'
import type { CampaignAnswerDraft } from '@/types/campaign'
import { getApiErrorMessage } from '@/utils/api-error'
import { formatLocalDate } from '@/utils/campaign-dates'
import { isAnswerEmpty, isQuestionVisible } from '@/utils/campaign-visibility'

/** Formulário de uma campanha, aberto pelo link compartilhado (qualquer usuário logado do SSMA). */
export const CampaignRespondPage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id = '' } = useParams<{ id: string }>()

	const { data: campaign, isLoading, error } = useCampaignForReader(id)
	const submitMutation = useSubmitCampaignResponse(id)

	const [answers, setAnswers] = useState<Record<string, CampaignAnswerDraft>>(
		{},
	)
	const [missing, setMissing] = useState<Set<string>>(new Set())
	const [submitted, setSubmitted] = useState(false)

	const questionsById = useMemo(
		() => new Map((campaign?.questions ?? []).map((q) => [q.id, q])),
		[campaign],
	)
	const visibleQuestions = useMemo(
		() =>
			(campaign?.questions ?? []).filter((question) =>
				isQuestionVisible(question, questionsById, answers),
			),
		[campaign, questionsById, answers],
	)

	const goBack = () => navigate(ROUTES.DASHBOARD.CAMPAIGNS)

	const handleChange = (questionId: string, value: CampaignAnswerDraft) => {
		setAnswers((current) => ({ ...current, [questionId]: value }))
		setMissing((current) => {
			if (!current.has(questionId)) return current
			const next = new Set(current)
			next.delete(questionId)
			return next
		})
	}

	const handleSubmit = async () => {
		if (!campaign) return

		const unanswered = visibleQuestions.filter(
			(question) =>
				question.required && isAnswerEmpty(question, answers[question.id]),
		)
		if (unanswered.length > 0) {
			setMissing(new Set(unanswered.map((question) => question.id)))
			toast.error(t('campaigns.respond.requiredError'))
			return
		}

		// perguntas que sumiram por causa de uma condição não vão no envio
		const visibleIds = new Set(visibleQuestions.map((question) => question.id))
		const payload = Object.fromEntries(
			Object.entries(answers).filter(([questionId]) =>
				visibleIds.has(questionId),
			),
		)

		try {
			await submitMutation.mutateAsync({
				questions: campaign.questions,
				answers: payload,
			})
			setSubmitted(true)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const resetForAnotherResponse = () => {
		setAnswers({})
		setMissing(new Set())
		setSubmitted(false)
	}

	const status =
		error instanceof AxiosError ? error.response?.status : undefined
	const blockedMessage = !campaign
		? null
		: campaign.availability === 'SCHEDULED'
			? t('campaigns.respond.scheduled', {
					date: campaign.startsAt ? formatLocalDate(campaign.startsAt) : '',
				})
			: campaign.availability === 'ENDED'
				? t('campaigns.respond.ended')
				: !campaign.canRespond
					? t('campaigns.respond.alreadyAnswered')
					: null

	return (
		<AdminCardPage
			icon={<ClipboardList className="size-5" />}
			title={campaign?.title ?? t('campaigns.title')}
			subtitle={campaign?.description ?? undefined}
			onBack={goBack}
			footerActions={
				campaign?.canRespond && !submitted ? (
					<Button
						type="button"
						className="w-auto bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
						disabled={submitMutation.isPending}
						onClick={handleSubmit}
					>
						{submitMutation.isPending ? <Spinner /> : null}
						{submitMutation.isPending
							? t('campaigns.respond.sending')
							: t('campaigns.respond.submit')}
					</Button>
				) : undefined
			}
		>
			{isLoading ? (
				<p className="py-16 text-center text-muted-foreground text-sm">
					{t('common.loading')}
				</p>
			) : !campaign ? (
				<p className="py-16 text-center text-destructive text-sm">
					{status === 403
						? t('campaigns.respond.forbidden')
						: status === 404
							? t('campaigns.respond.notFound')
							: t('campaigns.loadError')}
				</p>
			) : submitted ? (
				<div className="flex flex-col items-center gap-3 py-12 text-center">
					<CheckCircle2 className="size-12 text-kaefer-forest-green" />
					<h2 className="text-lg font-semibold text-kaefer-dark-grey">
						{t('campaigns.respond.successTitle')}
					</h2>
					<p className="text-sm text-muted-foreground">
						{t('campaigns.respond.successDescription')}
					</p>
					<div className="flex gap-2">
						<Button
							type="button"
							variant="outline"
							className="w-auto"
							onClick={goBack}
						>
							{t('campaigns.respond.backToList')}
						</Button>
						{campaign.allowMultiple && (
							<Button
								type="button"
								className="w-auto"
								onClick={resetForAnotherResponse}
							>
								{t('campaigns.respond.answerAgain')}
							</Button>
						)}
					</div>
				</div>
			) : blockedMessage ? (
				<p className="py-16 text-center text-muted-foreground text-sm">
					{blockedMessage}
				</p>
			) : (
				<div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
					{visibleQuestions.map((question, index) => (
						<div
							key={question.id}
							className={
								missing.has(question.id)
									? 'rounded-lg border border-destructive bg-white p-4'
									: 'rounded-lg border border-kaefer-light-silver bg-white p-4'
							}
						>
							<p className="text-sm font-semibold text-kaefer-dark-grey">
								{index + 1}. {question.label}
								{question.required && (
									<span
										className="ml-1 text-destructive"
										title={t('campaigns.respond.required')}
									>
										*
									</span>
								)}
							</p>
							{question.helpText && (
								<p className="mt-0.5 text-xs text-muted-foreground">
									{question.helpText}
								</p>
							)}
							<div className="mt-3">
								<CampaignQuestionInput
									question={question}
									value={answers[question.id]}
									onChange={(value) => handleChange(question.id, value)}
									onFileError={(message) => toast.error(message)}
								/>
							</div>
							{missing.has(question.id) && (
								<p className="mt-2 text-xs text-destructive">
									{t('campaigns.respond.requiredQuestion')}
								</p>
							)}
						</div>
					))}
				</div>
			)}
		</AdminCardPage>
	)
}
